import {
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePasteAssetClipboard } from '@frameio/assets/finder';
import { getFolderColorMix, useFolderColor } from '@frameio/assets/folder';
import {
  useIsAssetDragging,
  useIsFolderMoveTarget,
} from '@frameio/assets/operations';
import { useFolderNameFragment } from '@frameio/graphql-client/generated';
import { usePrefetchFolderPermissions } from '@frameio/permissions';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useForceGlobalDndRemeasure } from '@frameio/shared/dnd';
import { useIsLabsFeatureEnabled } from '@frameio/shared/hooks';
import { getLink } from '@frameio/shared/routing';
import { useIsUploadLinksEnabled } from '@frameio/upload-links';
import {
  IconButton2,
  mergeProps,
  theme,
  useFocusWithin,
  useHoverIntent,
  useThemeName,
} from '@frameio/vapor';
import {
  IconFolderClosed,
  IconFolderLocked,
  IconFolderOpen,
  IconFolderOpenLocked,
  IconMoreHorizontal,
} from '@frameio/vapor-icons';
import {
  useBaseDepth,
  useFolderDndNamespace,
  useIsActiveItem,
  useIsContextMenuDisabled,
  useIsFolderUploadTarget,
  useOnItemSelect,
  useOpenNavigationMenu,
  useShouldRenderLinks,
  useToggleItem,
} from '../../contexts';
import type { FolderTreeItemProps, ProjectTreeItem } from '../../types';
import {
  BaseContainer,
  calculatePadding,
  DraggableContainer,
  HiddenLink,
  ICON_WIDTH,
  ItemContainer,
  ItemSkeleton,
} from '../TreeItem';
import { FolderName } from './FolderName';
import TEXT from './generated/TEXT/ProjectTree_Folder';

export const Folder = ({
  depth,
  id,
  isLoading,
  isOpen,
  transition,
  type,
}: FolderTreeItemProps) => {
  const { data: folder } = useFolderNameFragment({
    from: {
      __typename: 'Asset',
      id,
    },
  });
  const item = useMemo<ProjectTreeItem>(() => ({ id, type }), [id, type]);

  const baseDepth = useBaseDepth();
  const toggleItem = useToggleItem();
  const onItemSelect = useOnItemSelect();
  const isActive = useIsActiveItem(item);
  const isRestricted = folder?.restricted ?? false;
  const isDisabled = transition === 'close';
  const projectId = useActiveProjectId();
  const shouldRenderLink = useShouldRenderLinks();
  const dndNamespace = useFolderDndNamespace();
  const isDragging = useIsAssetDragging() && dndNamespace !== null;
  const contextMenuDisabled = useIsContextMenuDisabled();
  const isFolderMoveTarget = useIsFolderMoveTarget(id) && dndNamespace !== null;
  const isFolderUploadTarget =
    useIsFolderUploadTarget(id) && dndNamespace === 'project-nav';
  const isFolderDndTarget = isFolderMoveTarget || isFolderUploadTarget;
  const pasteClipboard = usePasteAssetClipboard();
  const isColoredFolders = useIsLabsFeatureEnabled('folder_colors');
  const { colorToken } = useFolderColor(id);
  const themeName = useThemeName();
  const isLight = themeName === 'light' || themeName === 'light-accentNeutral';
  const iconColor =
    isColoredFolders && colorToken
      ? getFolderColorMix(colorToken, isLight)
      : theme.colors.icon.primary;

  const href = useMemo(
    () =>
      shouldRenderLink
        ? getLink({
            page: 'folder',
            projectId: projectId ?? '',
            folderAssetId: id,
          })
        : undefined,
    [id, projectId, shouldRenderLink]
  );

  const [focused, setFocused] = useState(false);
  const iconRef = useRef<HTMLButtonElement | null>(null);
  const openNavigationMenu = useOpenNavigationMenu();

  const { focusWithinProps } = useFocusWithin({
    isDisabled,
    onFocusWithinChange: setFocused,
  });

  // Warm this folder's permissions on hover-intent so the "Create Upload Link"
  // context-menu item is already resolved by the time the menu opens instead of
  // fetching once it's open.
  const isUploadLinksEnabled = useIsUploadLinksEnabled();
  const prefetchFolderPermissions = usePrefetchFolderPermissions();
  const handleUploadLinkHoverIntent = useCallback(() => {
    if (isUploadLinksEnabled) prefetchFolderPermissions(id);
  }, [id, isUploadLinksEnabled, prefetchFolderPermissions]);

  const { bind: uploadLinkHoverIntent } = useHoverIntent(
    handleUploadLinkHoverIntent
  );

  const LockedFolderIcon = isActive ? IconFolderOpenLocked : IconFolderLocked;
  const PublicFolderIcon = isActive ? IconFolderOpen : IconFolderClosed;
  const FolderIcon = isRestricted ? LockedFolderIcon : PublicFolderIcon;
  const paddingLeft = calculatePadding(baseDepth, depth);

  const handleToggle = () => {
    toggleItem?.(item, !isOpen);
  };

  const handleContextMenu = (event?: React.MouseEvent) => {
    event?.preventDefault();

    if (contextMenuDisabled) return false;

    openNavigationMenu(item, iconRef, event);

    return true;
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    let didHandleEvent = false;

    if (event.key === 'Enter') {
      onItemSelect(item, href);
      didHandleEvent = true;
    }

    if (event.key === 'ContextMenu') {
      handleContextMenu();
      didHandleEvent = true;
    }

    if (event.key === 'ArrowRight' && !isOpen) {
      toggleItem?.(item, true);
      didHandleEvent = true;
    }

    if (event.key === 'ArrowLeft' && isOpen) {
      toggleItem?.(item, false);
      didHandleEvent = true;
    }

    if (event.metaKey && event.key === 'v') {
      pasteClipboard(id);
      didHandleEvent = true;
    }

    if (didHandleEvent) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * when an item is dragged over this folder, if it's closed start a
   * timer. when the timer is up, toggle it open
   */
  const forceDndRemeasure = useForceGlobalDndRemeasure();

  useEffect(() => {
    let timer: number;
    if (!isOpen && isFolderDndTarget) {
      timer = window.setTimeout(() => {
        toggleItem?.(item, true);
        forceDndRemeasure();
      }, LONG_HOLD_TOGGLE_DELAY);
    }
    return () => window.clearTimeout(timer);
  }, [isOpen, isFolderDndTarget, item, toggleItem, forceDndRemeasure]);

  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button === 2 || event.metaKey) return;

      onItemSelect(item, href);
    },
    [href, item, onItemSelect]
  );

  const mergedHandlers = mergeProps(
    focusWithinProps,
    {
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      onContextMenu: handleContextMenu,
    },
    isUploadLinksEnabled ? uploadLinkHoverIntent : {}
  );

  if (!folder || !folder.name)
    return <ItemSkeleton paddingLeft={paddingLeft} />;

  return (
    <DraggableContainer
      key={id}
      $dndVariant={isFolderDndTarget ? 'over' : undefined}
      $isActive={isActive}
      as={BaseContainer}
      style={{ opacity: isDragging ? DRAGGING_FOLDER_OPACITY : 1 }}
      {...mergedHandlers}
    >
      {shouldRenderLink && href && (
        <HiddenLink
          aria-label={folder.name ?? undefined}
          href={href}
          tabIndex={-1}
        />
      )}

      <ItemContainer
        adornmentAfter={
          !contextMenuDisabled && (
            <IconButton2
              ref={iconRef}
              icon={<IconMoreHorizontal />}
              label={TEXT.moreOptions}
              onPress={() => handleContextMenu()}
              size="small"
              variant="tertiary"
            />
          )
        }
        expandable={true}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isLink={shouldRenderLink}
        isLoading={isLoading}
        isOpen={isOpen}
        name={folder.name}
        onChevronClick={handleToggle}
        paddingLeft={paddingLeft}
        type={type}
      >
        <FolderIcon
          color={iconColor}
          size={ICON_WIDTH}
          style={{ flexShrink: 0 }}
        />
        <FolderName focused={focused} isActive={isActive} item={item} />
      </ItemContainer>
    </DraggableContainer>
  );
};

/**
 * delay to wait while dragging an item over a folder before toggling
 * an expand/collapse
 * */
export const LONG_HOLD_TOGGLE_DELAY = 1200; //ms

// Value here matches the opacity of the finder grid when dragging
const DRAGGING_FOLDER_OPACITY = 0.2;

TEXT.DEFINE({
  /** aria-label for the more options button for folders in the project tree. */
  moreOptions: 'More options',
});
